import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  IconButton,
  Box,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import toast from 'react-hot-toast';
import { Button } from '../../../../components/Button/Button';
import axiosInstant from '../../../../api';
import { createApiKey } from '../../../../api/endpoints';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  // Internal staff creating a key on a brand's behalf; omitted for brand users.
  brandId?: number;
  // Personal API Keys reuse this dialog with their own endpoint and copy;
  // the Public API Keys page passes neither.
  createUrl?: string;
  description?: string;
  // Personal API Keys only: the API asks for the caller's password before
  // minting a key that never expires.
  requirePassword?: boolean;
}

// Personal-key create errors (lib/controllers/serviceTokens.js).
const CREATE_ERRORS: Record<string, string> = {
  PasswordRequired: 'Enter your password to generate a key.',
  PasswordIncorrect: 'That password is incorrect.',
  PasswordNotSet:
    'Your account has no password. Set one under Reset Password first.',
  rate_limit_exceeded: 'Too many attempts. Try again in 15 minutes.',
};

export const CreateApiKeyDialog: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  brandId,
  createUrl = createApiKey(),
  description = 'Generate an API key for accessing the product lookup API.',
  requirePassword = false,
}) => {
  const [label, setLabel] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const res = await axiosInstant.post(createUrl, {
        label: label.trim() || undefined,
        brand_id: brandId,
        ...(requirePassword ? { password } : {}),
      });
      const key = res.data?.key || res.data?.data?.key;
      setGeneratedKey(key);
      setPassword('');
      onCreated();
      toast.success('API key created');
    } catch (err: any) {
      const code = err?.response?.data?.error;
      toast.error(
        (code && CREATE_ERRORS[code]) ||
          err?.response?.data?.message ||
          'Failed to create API key'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedKey) return;
    try {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleClose = () => {
    setPassword('');
    if (generatedKey) {
      setGeneratedKey(null);
      setLabel('');
      setCopied(false);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={generatedKey ? undefined : handleClose}
      disableEscapeKeyDown={!!generatedKey}
      PaperProps={{
        sx: {
          m: { xs: 1.5, sm: 4 },
          width: { xs: 'calc(100% - 24px)', sm: 'auto' },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <Typography variant="h5" className="text-[20px]">
            {generatedKey ? 'Your New API Key' : 'Generate API Key'}
          </Typography>
          {!generatedKey && (
            <IconButton onClick={handleClose}>
              <CloseIcon className="text-text-2" />
            </IconButton>
          )}
        </div>
      </DialogTitle>

      <DialogContent>
        {generatedKey ? (
          <div className="flex flex-col gap-4">
            <Alert severity="warning" sx={{ mb: 1 }}>
              This key will only be shown once. Copy it now and store it
              securely.
            </Alert>

            {/* `gray-100` does not exist in this Tailwind config (the `gray`
                key is a single literal), so this box renders transparent
                today; bg-surface-2 would newly paint it. Left as-is. */}
            <Box
              className="flex items-center gap-2 bg-gray-100 rounded p-3"
              sx={{ wordBreak: 'break-all' }}
            >
              <code className="text-[13px] font-mono flex-1">
                {generatedKey}
              </code>
              <IconButton size="small" onClick={handleCopy}>
                {copied ? (
                  <CheckIcon fontSize="small" color="success" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </div>
        ) : (
          <div className="flex flex-col gap-4 pt-1">
            <Typography className="text-text-2 text-[14px]">
              {description}
            </Typography>
            <TextField
              label="Label (optional)"
              placeholder="e.g. Production Integration"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 100 }}
            />
            {requirePassword && (
              <TextField
                label="Your password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                size="small"
                required
              />
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions className="!px-6 !py-4">
        {generatedKey ? (
          <Button variant="contained" color="primary" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerate}
              disabled={loading || (requirePassword && !password)}
            >
              {loading ? 'Generating...' : 'Generate Key'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
